import {
  faArrowUpArrowDown,
  faBell,
  faChevronRight,
  faFile,
  faList,
  faMemoPad,
  faShareNodes,
  faUserGroup,
  faXmark,
  faHardDrive,
  faKey,
  faTags,
} from "@fortawesome/pro-regular-svg-icons";
import {
  getCurrentRegion,
  getIsOnpremise,
  getIsPatientDeletionEnabled,
  hasSiteKeyword,
  isWebView,
} from "../../utils";
import { trackEvent } from "../../utils/mixpanel";
import AwesomeIcon from "../AwesomeIcon";
import classNames from "classnames";
import { t } from "i18next";
import Store from "../../utils/Store";
import { Fragment, useContext, useState } from "react";
import { GatewayContext } from "../GatewayIndicator/GatewayContext";
import { useRouter } from "next/router";
import useMedia, { MediaSizes } from "../../hooks/useMedia";
import UserBio from "./UserBio";
import constants from "../../utils/constants";
import Toggle from "../common/Toggle";
import { get } from "lodash";
import { useStoreState } from "pullstate";
import useSaveNotificationSettings from "../NotificationSettings/services/useSaveNotificationSettings";
import Toast from "../Toast";
import {
  faDownload,
  faInfoCircle,
  faLocationDot,
  faMobileNotch,
  faSunBright,
  faUsersMedical,
  faWifi,
  faMobileSignalOut,
} from "@fortawesome/pro-regular-svg-icons";
import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons";
import { buildStyles, CircularProgressbar } from "react-circular-progressbar";
import TermsAndConditionsModal from "../TermsAndConditionsModal";
import { subscribeUserNotification } from "../../utils/pushNotification";
import Loader from "../Loader";
import { MIXPANEL_EVENTS } from "../../utils/mixpanelEvents";
import { LicenseContext } from "../LicenseManager/LicenseContext";
import getEnv from "../../utils/getEnv";
import { GatewayContextProps } from "../GatewayIndicator/types";
import { LicenseContextType } from "../../types/license-context";
import { useAuth } from "@qure/qure-auth";

interface SettingsMenuProps {
  onClose?: () => void;
  onLogout?: () => void;
  onChange?: (pageName: string) => void;
  selected?: string;
}

const NEXT_PUBLIC_ENABLE_SCAN_TO_CONNECT =
  getEnv("NEXT_PUBLIC_ENABLE_SCAN_TO_CONNECT") === "true";
const SettingsMenu = (props: SettingsMenuProps) => {
  const { onLogout, onClose = () => {}, onChange, selected } = props;
  const [showTermsAndConditionsModal, setShowTermsAndConditionsModal] = useState(false);
  const [isSubscribeLoading, setSubscribeLoading] = useState(false);
  const isMobile = useMedia([MediaSizes.MOBILE], [true], false);
  const isOnpremise = getIsOnpremise();
  const isPatientDeletionEnabled = getIsPatientDeletionEnabled();
  const { offlineGatewayCount, totalGateway } =
    useContext<GatewayContextProps>(GatewayContext);
  const router = useRouter();
  const { alertDetails } = useContext<LicenseContextType>(LicenseContext);
  const { userInfo, refetchProfile, isLoading: isProfileLoading } = useAuth();

  const {
    isNotificationSubscribing,
    isDarkMode,
    isExportReady,
    downloadProgress,
    isStartingDownload,
    exportGenFailed,
    isRNTestBuild,
    RNSelectedEnv,
    RNfcmToken,
    fcmToken,
    isDiskSpaceWarning,
    appConfig,
  } = useStoreState(Store, (store) => ({
    isNotificationSubscribing: store.isNotificationSubscribing,
    isDarkMode: store.isDarkMode,
    isExportReady: store.isExportReady,
    downloadProgress: store.downloadProgress,
    isStartingDownload: store.isStartingDownload,
    exportGenFailed: store.exportGenFailed,
    isRNTestBuild: store.isRNTestBuild,
    RNSelectedEnv: store.RNSelectedEnv,
    RNfcmToken: store.RNfcmToken,
    fcmToken: store.fcmToken,
    isDiskSpaceWarning: store.isDiskSpaceWarning,
    appConfig: store.appConfig,
  }));

  const saveNotificationSettings = useSaveNotificationSettings();
  const isLoggedIn = get(userInfo, ["authenticated"], false);
  const isMobileApp = window?.ReactNativeWebView;
  const isNotificationOn =
    !isLoggedIn || !get(userInfo, ["settings", "notification_snoozed"], false);
  const token = RNfcmToken || fcmToken;
  const hasPermission =
    (isMobileApp && !!RNfcmToken) || window?.Notification?.permission === "granted";
  const isSubscribed =
    isNotificationSubscribing ||
    (!isLoggedIn && hasPermission) ||
    (!token && hasPermission) ||
    get(userInfo, ["subscription_tokens"], []).some(
      (tokenObj) => get(tokenObj, ["token"], "") === token
    );
  const isLicenseEnabled = get(userInfo, ["settings", "is_licensing_enabled"], false);
  const isCustomTagsEnabled = get(appConfig, ["custom_tags_filter"], false);

  const settings = [
    {
      pageName: constants.SETTINGS.THEME,
      section: "user_settings",
      icon: faSunBright,
      viewOnly: true,
      className: isMobile ? "hidden" : "",
      control: () => {
        return (
          <div
            className={classNames("flex items-center p-4 gap-4 cursor-pointer")}
            onClick={onToggleThemeClick}
          >
            <div className="text-sm uppercase">
              {isDarkMode ? t("settings.dark") : t("settings.light")}
            </div>
            <Toggle value={!isDarkMode || false} onChange={onToggleThemeClick} />
          </div>
        );
      },
    },
    {
      pageName: constants.SETTINGS.NOTIFICATIONS,
      section: "user_settings",
      icon: faBell,
      className: classNames({ hidden: isOnpremise }),
      control: () => {
        return (
          <div
            className={classNames(
              "flex sm:hidden items-center border-l border-gray-800 p-4 gap-4"
            )}
          >
            <div>{t("settings.on")}</div>
            <Toggle
              value={isNotificationOn}
              onChange={onToggleNotification}
              disabled={
                !isSubscribed ||
                !hasPermission ||
                saveNotificationSettings.isLoading ||
                isProfileLoading ||
                isNotificationSubscribing
              }
            />
          </div>
        );
      },
    },
    {
      pageName: constants.SETTINGS.WORKLIST_TAGS,
      section: "user_settings",
      icon: faList,
    },
    {
      pageName: constants.SETTINGS.FORM_TEMPLATES,
      section: "user_settings",
      icon: faMemoPad,
    },
    {
      pageName: constants.SETTINGS.AUTOMATED_SHARING,
      section: "user_settings",
      icon: faShareNodes,
      className: classNames({ hidden: isOnpremise }),
    },
    {
      pageName: constants.SETTINGS.USER_GROUPS,
      section: "user_settings",
      icon: faUserGroup,
      className: classNames({ hidden: isOnpremise }),
    },
    {
      pageName: constants.SETTINGS.CUSTOM_TAGS_MANAGEMENT,
      section: "user_settings",
      icon: faTags,
      className: classNames({ hidden: isOnpremise || !isCustomTagsEnabled }),
    },
    {
      pageName: hasSiteKeyword()
        ? constants.SETTINGS.SITE_MANAGEMENT
        : constants.SETTINGS.WORKSPACE_MANAGEMENT,
      section: "user_settings",
      icon: faUsersMedical,
      className: "",
    },
    {
      pageName: constants.SETTINGS.REPORT_TEMPLATE,
      section: "user_settings",
      icon: faFile,
      className: classNames({ hidden: isMobile }),
    },
    {
      pageName: constants.SETTINGS.DISK_MANAGEMENT,
      section: "user_settings",
      icon: faHardDrive,
      className: classNames({
        hidden: isMobile || !(isOnpremise && isPatientDeletionEnabled),
      }),
      control: () => {
        if (isDiskSpaceWarning) {
          return (
            <div className={classNames("flex items-center p-4 gap-4")}>
              <AwesomeIcon name={faTriangleExclamation} className="text-yellow-500" />
            </div>
          );
        }
        return null;
      },
    },
    {
      pageName: constants.SETTINGS.LICENSE_MANAGER,
      section: "user_settings",
      icon: faKey,
      control: () => {
        if (alertDetails?.isAlert) {
          return (
            <AwesomeIcon name={faTriangleExclamation} className="text-yellow-500 mr-5" />
          );
        }
        return null;
      },
      className: classNames({
        // hidden: isMobile || !isOnpremise || !isLicenseEnabled,
        hidden: isMobile || !isLicenseEnabled,
      }),
    },
    {
      pageName: constants.SETTINGS.EXPORT_DATA,
      section: "more",
      icon: faDownload,
      control: () => {
        let status = <></>;
        if (
          isStartingDownload ||
          (downloadProgress > 0 && downloadProgress < 100 && !isExportReady)
        ) {
          status = (
            <div className="w-6 h-6">
              <CircularProgressbar
                value={downloadProgress}
                styles={buildStyles({
                  rotation: 0.25,
                  strokeLinecap: "butt",
                  textSize: "24px",
                  pathTransitionDuration: 0.5,
                  pathColor: `rgba(0, 130, 128)`,
                  textColor: "#f88",
                  trailColor: isDarkMode ? "white" : "#b6aeae",
                  backgroundColor: "white",
                })}
              />
            </div>
          );
        } else if (exportGenFailed) {
          status = (
            <div className="flex text-md text-red-600 dark:text-red-400 font-normal">
              {t("settings.export_failed")}
            </div>
          );
        }
        return <div className="cursor-pointer mr-3">{status}</div>;
      },
      className: classNames({
        hidden: isMobile,
      }),
    },
    {
      pageName: constants.SETTINGS.GATEWAY,
      section: "more",
      icon: faArrowUpArrowDown,
      count: offlineGatewayCount,
      className: classNames({ hidden: !totalGateway }),
    },
    // {
    //   pageName: constants.SETTINGS.MODALITY_CONNECT,
    //   section: "more",
    //   icon: faNetworkWired,
    //   className: classNames({ hidden: isMobile || isOnpremise }),
    // },
    {
      pageName: constants.SETTINGS.SCAN_TO_CONNECT,
      section: "more",
      icon: faMobileSignalOut,
      className: classNames({
        hidden: !isOnpremise || !NEXT_PUBLIC_ENABLE_SCAN_TO_CONNECT,
      }),
    },
    {
      pageName: constants.SETTINGS.REMOTE_ACCESS,
      section: "more",
      icon: faWifi,
      className: classNames({ hidden: !isOnpremise }),
    },
    {
      pageName: constants.SETTINGS.TERMS_OF_USE,
      section: "more",
      icon: faInfoCircle,
      className: classNames({
        hidden: !isMobile,
      }),
      onClick: () => {
        setShowTermsAndConditionsModal(true);
        trackEvent(MIXPANEL_EVENTS.OPEN_TERMS_OF_USE);
      },
    },
    {
      pageName: constants.SETTINGS.REGION,
      customLabel: t(getCurrentRegion()),
      section: "more",
      icon: faLocationDot,
      viewOnly: true,
      className: classNames("cursor-default", {
        hidden: isOnpremise || !isMobile,
      }),
    },
  ];

  const onToggleThemeClick = () => {
    if (window) {
      localStorage.setItem(constants.PERSISTED.isDarkMode, String(!isDarkMode));
      Store.update((s) => {
        s.isDarkMode = !isDarkMode;
      });
      trackEvent(MIXPANEL_EVENTS.THEME_CHANGED, {
        isDarkMode: !isDarkMode,
      });
    }
  };

  const onToggleNotification = async (value: boolean) => {
    try {
      let payload = { notification_snoozed: !value };
      await saveNotificationSettings.mutateAsync(payload);
      await refetchProfile();
      trackEvent(MIXPANEL_EVENTS.UPDATE_NOTIFICATION_SETTINGS, {
        notification_snoozed: payload.notification_snoozed,
      });
    } catch (err) {
      Toast.error("Something went wrong. Please retry");
    }
  };

  const onChangeSetting = (pageName: string) => {
    if (onChange) {
      onChange(pageName);
    } else {
      router.push(`/settings/${pageName}`, undefined, { shallow: true });
    }
    trackEvent(MIXPANEL_EVENTS.SELECT_SETTING, {
      pageName,
    });
  };

  const onUpdateSubscription = async () => {
    if (isMobileApp && !hasPermission) {
      const data = {
        event: constants.RN_COMM_CONST.OPEN_APP_SETTINGS,
        data: null,
      };
      window?.ReactNativeWebView?.postMessage(JSON.stringify(data));
    } else {
      setSubscribeLoading(true);
      const subscription = await subscribeUserNotification(RNfcmToken);
      if (subscription) {
        await refetchProfile();
        setSubscribeLoading(false);
      }
      setSubscribeLoading(false);
    }
  };

  let _section = "";
  return (
    <div
      className={classNames(
        "flex flex-col h-full bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200 w-full sm:w-2/5",
        {
          "pt-6": isWebView(),
        }
      )}
    >
      <div className="flex items-center justify-start p-4 sm:pl-6 text-lg border-b border-gray-300 dark:border-gray-700">
        <button onClick={onClose} className="flex sm:hidden p-4">
          <AwesomeIcon name={faXmark} className="h-6 w-6 " />
        </button>
        <div>{t("settings_key")}</div>
      </div>
      {!isOnpremise && isMobile && (
        <>
          {(!hasPermission || !isSubscribed) && (
            <div className="inline bg-yellow-900 text-md p-4 py-5">
              {isSubscribeLoading ? (
                <Loader
                  size="medium"
                  label="Please wait"
                  className="mr-3"
                  containerClassName="!py-0 flex !flex-row"
                />
              ) : (
                <>
                  <span className="pr-2">
                    {t("notification_modal.no_notifications_setting")}
                  </span>
                  <span
                    className="text-yellow-400 font-bold cursor-pointer"
                    onClick={onUpdateSubscription}
                  >
                    {t("notification_modal.update_settings")}
                  </span>
                </>
              )}
            </div>
          )}
        </>
      )}
      <UserBio onLogout={onLogout} />

      <div className="flex flex-col h-full overflow-auto scrollbar-light dark:scrollbar">
        {settings.map((_setting: any, idx) => {
          const {
            className = "",
            section,
            pageName,
            count,
            icon,
            viewOnly,
            control,
            onClick,
            customLabel,
          } = _setting;
          let header = null;
          if (section !== _section) {
            _section = section;
            header = (
              <div className="flex items-center font-semibold uppercase text-gray-800 dark:text-gray-300 py-3 px-5 mt-3">
                {t(`settings.${section}`)}
                <hr className="flex grow border-gray-300 dark:border-gray-700 ml-3" />
              </div>
            );
          }
          return (
            <Fragment key={pageName}>
              {header}
              <div
                className={classNames("flex items-center justify-between ", {
                  "hover:bg-gray-200 hover:text-gray-800 dark:hover:bg-gray-800 dark:hover:text-gray-200 cursor-pointer":
                    !viewOnly,
                  "bg-gray-200 dark:bg-gray-800 text-gray-800 dark:text-gray-200":
                    pageName === selected,
                  [className]: !!className,
                })}
                data-testid={`${pageName}-setting`}
                onClick={() => {
                  if (!viewOnly) {
                    if (onClick) {
                      onClick(pageName);
                    } else {
                      onChangeSetting(pageName);
                    }
                  }
                }}
              >
                <div
                  className={classNames(
                    "flex py-4 items-center justify-between px-5 w-full",
                    {}
                  )}
                >
                  <div className="flex items-center justify-start">
                    <div className="relative">
                      {count && count > 0 && (
                        <div className="absolute border-gray-800 flex justify-center items-center text-sm rounded-full bg-red-600 text-gray-100 right-4 font-medium -top-1 w-5 h-5">
                          {count}
                        </div>
                      )}
                      {icon && <AwesomeIcon name={icon} size="lg" className="mr-5 w-6" />}
                    </div>
                    <div className="flex items-center justify-start whitespace-nowrap">
                      {customLabel || t(`settings.${pageName}`)}
                    </div>
                  </div>
                  <AwesomeIcon
                    name={faChevronRight}
                    size="lg"
                    className={classNames("sm:hidden justify-self-end", {
                      hidden: viewOnly,
                    })}
                  />
                </div>
                {control && control()}
              </div>
            </Fragment>
          );
        })}
        {!!isRNTestBuild && (
          <div
            className=""
            onClick={() => {
              Store.update((s) => {
                s.isRNEnvSelectorOpen = true;
              });
            }}
          >
            <div className="flex p-4 items-center justify-start">
              <AwesomeIcon name={faMobileNotch} className="h-6 w-6 mr-5" />
              <div className="flex flex-col items-start justify-start">
                <div className="flex items-center justify-start">Debug Environment</div>
                <div className="flex justify-start text-sm">{RNSelectedEnv}</div>
              </div>
            </div>
          </div>
        )}
      </div>
      {showTermsAndConditionsModal && (
        <TermsAndConditionsModal
          onClose={() => {
            setShowTermsAndConditionsModal(false);
          }}
          showModal={showTermsAndConditionsModal}
          // closeOnClickAway
          mode="readonly"
        />
      )}
    </div>
  );
};

export default SettingsMenu;
